import { ApiRequestError, announcePlanLimit, isPlanLimitCode } from "@/lib/planLimit";
import { announceAccountClosed } from "@/lib/accountClosed";
import { announceTwoStepRequired } from "@/lib/twoStep";
import i18n from "i18next";
import type { TikzAskFigure, TikzAskProposal } from "@/lib/tikzAsk";
import { applyActAs } from "@/lib/testActAs";
import { logger } from "./logger";

const API_BASE = "/api";

export type BillingInterval = "month" | "year";

/** A plan from `GET /api/billing/plans`: prices in dollars, absent when free or not offered; `caps` -1 for unlimited. */
export interface BillingPlan {
  id: string;
  slug: string;
  displayName: string;
  monthlyPrice?: number | null;
  yearlyPrice?: number | null;
  caps: { maxDocs?: number; maxImportsPerMonth?: number; aiCreditsPerMonth?: number; [key: string]: number | undefined };
  features: string[];
}

/** An import link from the connector (API ConnectorFilesController). */
export interface ConnectorImportTicket {
  status: "waiting" | "done" | "failed";
  app: string;
  expiresAt: string;
  documentId: string | null;
  title: string | null;
  error: string | null;
}

interface ApiError {
  message: string;
  code?: string;
}

// Default fetch options to include credentials (cookies) with all requests.
// Auth to the C# API is JWT-Bearer, so we ALSO set an Authorization header
// via `tokenProvider` below (cookies alone are not accepted).
const fetchOptions: RequestInit = {
  credentials: "include",
};

// Module-level token provider — registered once by ApiProvider so this
// non-hook module can still send authenticated requests. The provider
// returns the current Stytch session JWT, or null if the user isn't
// signed in. Stytch's SDK refreshes the session transparently — the
// provider always returns the freshest token without explicit refresh.
type TokenProvider = () => Promise<string | null>;
let tokenProvider: TokenProvider | null = null;
let refreshFn: (() => Promise<void>) | null = null;

export function registerApiAuth(provider: TokenProvider, refresh?: () => Promise<void>) {
  tokenProvider = provider;
  refreshFn = refresh ?? null;
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error: ApiError = await response.json().catch(() => ({
      message: "An unexpected error occurred",
    }));
    logger.error("API error", {
      status: response.status,
      url: response.url,
      message: error.message,
    }, "api");
    if (isPlanLimitCode(error.code)) announcePlanLimit(error.code);
    if (error.code === "ACCOUNT_CLOSED") announceAccountClosed();
    if (error.code === "TWO_STEP_REQUIRED") announceTwoStepRequired();
    throw new ApiRequestError(error.message, error.code, response.status);
  }
  return response.json();
}

// Helper to merge fetch options. Stays synchronous so the 107+ existing
// `await authFetch(url, withCredentials(...))` call sites can flip to
// `await authFetch(url, withCredentials(...))` with nothing else changing.
function withCredentials(options?: RequestInit): RequestInit {
  return { ...fetchOptions, ...options };
}

/**
 * Drop-in replacement for `fetch()`. Adds the Stytch session JWT as an
 * `Authorization: Bearer ...` header (via the module-level tokenProvider)
 * and retries once with a forced refresh on a 401.
 */
async function authFetch(url: string, options?: RequestInit): Promise<Response> {
  const buildHeaders = async (): Promise<Headers> => {
    const headers = applyActAs(new Headers(options?.headers));
    if (tokenProvider) {
      try {
        const token = await tokenProvider();
        if (token) headers.set("Authorization", `Bearer ${token}`);
      } catch { /* no token, proceed */ }
    }
    return headers;
  };

  let response = await fetch(url, { ...(options ?? {}), headers: await buildHeaders() });

  if (response.status === 401 && refreshFn) {
    try { await refreshFn(); } catch { /* surface original 401 */ }
    response = await fetch(url, { ...(options ?? {}), headers: await buildHeaders() });
  }
  return response;
}

export const api = {
  // Documents
  documents: {
    list: async () => {
      const response = await authFetch(`${API_BASE}/documents`, withCredentials());
      const documents = await handleResponse<Document[]>(response);
      return { documents };
    },

    get: async (id: string) => {
      const response = await authFetch(`${API_BASE}/documents/${id}`, withCredentials());
      const document = await handleResponse<DocumentWithBlocks>(response);
      return { document };
    },

    create: async (data: CreateDocumentInput) => {
      const response = await authFetch(`${API_BASE}/documents`, withCredentials({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }));
      const document = await handleResponse<Document>(response);
      return { document };
    },

    /** Generate (and persist) the document's AI one-liner gist (phase 2a). */
    generateSummary: async (id: string): Promise<{ aiSummary: string | null }> => {
      const response = await authFetch(`${API_BASE}/documents/${id}/summary`, withCredentials({
        method: "POST",
      }));
      return handleResponse<{ aiSummary: string | null }>(response);
    },

    /** Deterministic title search over own + shared documents (⌘K, phase 2c). */
    search: async (q: string, limit = 8): Promise<DocumentListItem[]> => {
      const params = new URLSearchParams({ page: "1", pageSize: String(limit), search: q, sortBy: "updatedAt", sortDir: "desc" });
      const response = await authFetch(`${API_BASE}/documents?${params}`, withCredentials());
      const result = await handleResponse<PaginatedResult<DocumentListItem>>(response);
      return result.items ?? [];
    },

    // FT-SANDBOX-SCOPE: mint a real-but-throwaway playground document
    // (seeded sample blocks, is_playground=true). Excluded from lists /
    // quotas server-side, but loadable + editable by id — the authed
    // editor playground runs the real editor + sync against it.
    createPlayground: async () => {
      const response = await authFetch(`${API_BASE}/documents/playground`, withCredentials({
        method: "POST",
      }));
      const document = await handleResponse<DocumentWithBlocks>(response);
      return { document };
    },

    update: async (id: string, data: UpdateDocumentInput) => {
      const response = await authFetch(`${API_BASE}/documents/${id}`, withCredentials({
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }));
      const document = await handleResponse<Document>(response);
      return { document };
    },

    delete: async (id: string) => {
      const response = await authFetch(`${API_BASE}/documents/${id}`, withCredentials({
        method: "DELETE",
      }));
      if (response.ok) {
        return { success: true };
      }
      throw new Error("Failed to delete document");
    },

    trash: async (page = 1, pageSize = 20) => {
      const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
      const response = await authFetch(`${API_BASE}/documents/trash?${params}`, withCredentials());
      return handleResponse<PaginatedResult<TrashDocumentItem>>(response);
    },

    restore: async (id: string) => {
      const response = await authFetch(`${API_BASE}/documents/${id}/restore`, withCredentials({
        method: "POST",
      }));
      if (response.ok) return { success: true };
      throw new Error("Failed to restore document");
    },

    permanentDelete: async (id: string) => {
      const response = await authFetch(`${API_BASE}/documents/${id}/permanent`, withCredentials({
        method: "DELETE",
      }));
      if (response.ok) return { success: true };
      throw new Error("Failed to permanently delete document");
    },

    autoSave: async (id: string, data: AutoSaveInput) => {
      // Auto-save uses the blocks batch update endpoint
      const response = await authFetch(`${API_BASE}/documents/${id}/blocks/batch`, withCredentials({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ blocks: data.blocks }),
      }));
      await handleResponse<Block[]>(response);

      // Also update document title if changed
      if (data.title) {
        await authFetch(`${API_BASE}/documents/${id}`, withCredentials({
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ title: data.title }),
        }));
      }

      return {
        document: {
          id,
          title: data.title ?? "",
          status: data.status ?? "saved",
          lastAutoSavedAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        } as AutoSaveResponse
      };
    },

    versions: {
      list: async (documentId: string, params?: { limit?: number; offset?: number }) => {
        const searchParams = new URLSearchParams();
        if (params?.limit) searchParams.set("limit", String(params.limit));
        if (params?.offset) searchParams.set("offset", String(params.offset));

        const response = await authFetch(
          `${API_BASE}/documents/${documentId}/versions?${searchParams.toString()}`,
          withCredentials()
        );
        const versions = await handleResponse<DocumentVersion[]>(response);
        return { versions };
      },

      create: async (documentId: string, data?: { comment?: string }) => {
        const response = await authFetch(`${API_BASE}/documents/${documentId}/versions`, withCredentials({
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: data?.comment }),
        }));
        const version = await handleResponse<DocumentVersion>(response);
        return { version };
      },

      restore: async (documentId: string, versionId: string) => {
        const response = await authFetch(
          `${API_BASE}/documents/${documentId}/versions/${versionId}/restore`,
          withCredentials({
            method: "POST",
          })
        );
        const document = await handleResponse<DocumentWithBlocks>(response);
        return { document };
      },
    },
  },

  // Blocks
  blocks: {
    list: async (documentId: string) => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/blocks`, withCredentials());
      const blocks = await handleResponse<Block[]>(response);
      return { blocks };
    },

    create: async (documentId: string, data: Partial<Block>) => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/blocks`, withCredentials({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }));
      const block = await handleResponse<Block>(response);
      return { block };
    },

    update: async (documentId: string, blockId: string, data: Partial<Block>) => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/blocks/${blockId}`, withCredentials({
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }));
      const block = await handleResponse<Block>(response);
      return { block };
    },

    delete: async (documentId: string, blockId: string) => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/blocks/${blockId}`, withCredentials({
        method: "DELETE",
      }));
      if (response.ok) {
        return { success: true };
      }
      throw new Error("Failed to delete block");
    },

    batchUpdate: async (documentId: string, blocks: BatchUpdateBlock[]) => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/blocks/batch`, withCredentials({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ blocks }),
      }));
      const updatedBlocks = await handleResponse<Block[]>(response);
      return { blocks: updatedBlocks };
    },

    reorder: async (documentId: string, blockIds: string[]) => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/blocks/reorder`, withCredentials({
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ blockIds }),
      }));
      const blocks = await handleResponse<Block[]>(response);
      return { blocks };
    },

    convert: async (documentId: string, blockId: string, newType: string, signal?: AbortSignal) => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/blocks/${blockId}/convert`, withCredentials({
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ newType }),
        signal,
      }));
      const block = await handleResponse<Block>(response);
      return { block };
    },

    getLatex: async (documentId: string, blockId: string) => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/blocks/${blockId}/latex`, withCredentials());
      return handleResponse<{ latex: string }>(response);
    },

    // Tier 1 bulk-convert — fold N selected blocks into one (list / merged
    // paragraph) or re-level a run of headings. Returns created + deleted.
    batchConvert: async (
      documentId: string,
      dto: { blockIds: string[]; action: "to_list" | "to_ordered_list" | "merge_paragraph" | "reheading"; headingLevel?: number },
      signal?: AbortSignal,
    ): Promise<{ created: Block[]; deletedIds: string[] }> => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/blocks/batch-convert`, withCredentials({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(dto),
        signal,
      }));
      return handleResponse<{ created: Block[]; deletedIds: string[] }>(response);
    },

    // v2 "edit block as LaTeX" round-trip (#68). Sends a user-edited fragment
    // back to the server; the fragment parser maps it to the same block type
    // and writes the JSONB content. 422 carries parse diagnostics.
    fromLatex: async (documentId: string, blockId: string, latex: string, signal?: AbortSignal): Promise<Block> => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/blocks/${blockId}/from-latex`, withCredentials({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ latex }),
        signal,
      }));
      if (response.status === 422) {
        const body = await response.json().catch(() => ({ diagnostics: [] }));
        const err = new Error(body.message ?? "LaTeX fragment could not be parsed") as Error & {
          code?: string;
          diagnostics?: Array<{ line?: number; column?: number; severity: string; code: string; message: string }>;
        };
        err.code = body.code;
        err.diagnostics = body.diagnostics;
        throw err;
      }
      return handleResponse<Block>(response);
    },
  },

  // Block Types
  blockTypes: {
    list: async (query?: string, category?: string) => {
      const searchParams = new URLSearchParams();
      if (query) searchParams.set("query", query);
      if (category) searchParams.set("category", category);
      const qs = searchParams.toString();
      const response = await authFetch(`${API_BASE}/blocktypes${qs ? `?${qs}` : ""}`, withCredentials());
      const blockTypes = await handleResponse<BlockTypeMetadata[]>(response);
      return { blockTypes };
    },
  },

  // Block groups (LILIA-136) — M:N grouping primitive scoped to a
  // document and a single dimension. First user is "layout" (multi-
  // column regions); designed to host other dimensions later.
  groups: {
    list: async (documentId: string) => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/groups`, withCredentials());
      const groups = await handleResponse<BlockGroup[]>(response);
      return { groups };
    },

    get: async (documentId: string, groupId: string) => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/groups/${groupId}`, withCredentials());
      const group = await handleResponse<BlockGroup>(response);
      return { group };
    },

    create: async (documentId: string, data: CreateBlockGroupInput) => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/groups`, withCredentials({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }));
      const group = await handleResponse<BlockGroup>(response);
      return { group };
    },

    update: async (documentId: string, groupId: string, data: UpdateBlockGroupInput) => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/groups/${groupId}`, withCredentials({
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }));
      const group = await handleResponse<BlockGroup>(response);
      return { group };
    },

    delete: async (documentId: string, groupId: string) => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/groups/${groupId}`, withCredentials({
        method: "DELETE",
      }));
      if (response.ok) return { success: true };
      throw new Error("Failed to delete group");
    },
  },

  // Bibliography
  bibliography: {
    list: async (documentId: string) => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/bibliography`, withCredentials());
      const entries = await handleResponse<Bibliography[]>(response);
      return { entries };
    },

    create: async (documentId: string, data: CreateBibliographyInput) => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/bibliography`, withCredentials({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }));
      const entry = await handleResponse<Bibliography>(response);
      return { entry };
    },

    update: async (documentId: string, entryId: string, data: Partial<CreateBibliographyInput>) => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/bibliography/${entryId}`, withCredentials({
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }));
      const entry = await handleResponse<Bibliography>(response);
      return { entry };
    },

    delete: async (documentId: string, entryId: string) => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/bibliography/${entryId}`, withCredentials({
        method: "DELETE",
      }));
      if (response.ok) {
        return { success: true };
      }
      throw new Error("Failed to delete bibliography entry");
    },

    import: async (documentId: string, bibTexContent: string) => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/bibliography/import`, withCredentials({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bibTexContent }),
      }));
      const entries = await handleResponse<Bibliography[]>(response);
      return { entries };
    },

    export: async (documentId: string) => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/bibliography/export`, withCredentials());
      const bibTex = await response.text();
      return { bibTex };
    },

    lookupDoi: async (documentId: string, doi: string) => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/bibliography/doi`, withCredentials({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ doi }),
      }));
      return handleResponse<DoiLookupResult>(response);
    },

    lookupIsbn: async (documentId: string, isbn: string) => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/bibliography/isbn`, withCredentials({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isbn }),
      }));
      return handleResponse<DoiLookupResult>(response);
    },

    lookupArxiv: async (documentId: string, arxivId: string) => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/bibliography/arxiv`, withCredentials({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ arxivId }),
      }));
      return handleResponse<DoiLookupResult>(response);
    },

    searchByTitle: async (documentId: string, query: string) => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/bibliography/search?q=${encodeURIComponent(query)}`, withCredentials());
      return handleResponse<DoiLookupResult[]>(response);
    },
  },

  /** The connector's drop page (MCP start_import): the file goes from the browser to Lilia. */
  connector: {
    importTicket: async (token: string) => {
      const response = await authFetch(`${API_BASE}/connector/imports/${encodeURIComponent(token)}`, withCredentials());
      return handleResponse<ConnectorImportTicket>(response);
    },
    importFile: async (token: string, file: { content: string; format: "LATEX" | "DOCX" | "LML"; filename: string }) => {
      const response = await authFetch(`${API_BASE}/connector/imports/${encodeURIComponent(token)}`, withCredentials({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(file),
      }));
      return handleResponse<{ documentId: string; title: string }>(response);
    },
  },

  // Jobs - handled by lilia-editor-api (C#/.NET)
  jobs: {
    list: async (params?: { status?: string; jobType?: string }) => {
      const searchParams = new URLSearchParams();
      if (params?.status) searchParams.set("status", params.status);
      if (params?.jobType) searchParams.set("jobType", params.jobType);

      const response = await authFetch(
        `${API_BASE}/lilia/jobs?${searchParams.toString()}`,
        withCredentials()
      );
      return handleResponse<{ jobs: Job[] }>(response);
    },

    get: async (id: string) => {
      const response = await authFetch(`${API_BASE}/lilia/jobs/${id}`, withCredentials());
      return handleResponse<{ job: Job }>(response);
    },

    retry: async (id: string) => {
      const response = await authFetch(`${API_BASE}/lilia/jobs/${id}/retry`, withCredentials({
        method: "POST",
      }));
      return handleResponse<{ job: Job }>(response);
    },

    import: async (data: ImportJobInput) => {
      const response = await authFetch(`${API_BASE}/lilia/jobs/import`, withCredentials({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }));
      return handleResponse<{ job: Job; document?: Document }>(response);
    },

    export: async (data: ExportJobInput) => {
      const response = await authFetch(`${API_BASE}/lilia/jobs/export`, withCredentials({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }));
      return handleResponse<{ job: Job; content: string; filename: string }>(
        response
      );
    },
  },

  // Templates
  templates: {
    list: async (params?: { category?: string }) => {
      const searchParams = new URLSearchParams();
      if (params?.category) searchParams.set("category", params.category);

      const response = await authFetch(
        `${API_BASE}/templates?${searchParams.toString()}`,
        withCredentials()
      );
      const templates = await handleResponse<Template[]>(response);
      return { templates };
    },

    get: async (id: string) => {
      const response = await authFetch(`${API_BASE}/templates/${id}`, withCredentials());
      const template = await handleResponse<TemplateWithBlocks>(response);
      return { template };
    },

    create: async (data: CreateTemplateInput) => {
      const response = await authFetch(`${API_BASE}/templates`, withCredentials({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }));
      const template = await handleResponse<Template>(response);
      return { template };
    },

    delete: async (id: string) => {
      const response = await authFetch(`${API_BASE}/templates/${id}`, withCredentials({
        method: "DELETE",
      }));
      if (response.ok) {
        return { success: true };
      }
      throw new Error("Failed to delete template");
    },

    use: async (id: string, data?: { title?: string }) => {
      const response = await authFetch(`${API_BASE}/templates/${id}/use`, withCredentials({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data ?? {}),
      }));
      const document = await handleResponse<Document>(response);
      return { document };
    },

    categories: async () => {
      const response = await authFetch(`${API_BASE}/templates/categories`, withCredentials());
      const categories = await handleResponse<TemplateCategory[]>(response);
      return { categories };
    },
  },

  // Labels
  labels: {
    list: async () => {
      const response = await authFetch(`${API_BASE}/labels`, withCredentials());
      const labels = await handleResponse<DocumentLabel[]>(response);
      return { labels };
    },

    create: async (data: CreateLabelInput) => {
      const response = await authFetch(`${API_BASE}/labels`, withCredentials({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }));
      const label = await handleResponse<DocumentLabel>(response);
      return { label };
    },

    update: async (id: string, data: Partial<CreateLabelInput>) => {
      const response = await authFetch(`${API_BASE}/labels/${id}`, withCredentials({
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }));
      const label = await handleResponse<DocumentLabel>(response);
      return { label };
    },

    delete: async (id: string) => {
      const response = await authFetch(`${API_BASE}/labels/${id}`, withCredentials({
        method: "DELETE",
      }));
      if (response.ok) {
        return { success: true };
      }
      throw new Error("Failed to delete label");
    },

    addToDocument: async (documentId: string, labelId: string) => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/labels/${labelId}`, withCredentials({
        method: "POST",
      }));
      if (response.ok) {
        return { success: true };
      }
      throw new Error("Failed to add label to document");
    },

    removeFromDocument: async (documentId: string, labelId: string) => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/labels/${labelId}`, withCredentials({
        method: "DELETE",
      }));
      if (response.ok) {
        return { success: true };
      }
      throw new Error("Failed to remove label from document");
    },
  },

  // Preferences
  preferences: {
    get: async () => {
      const response = await authFetch(`${API_BASE}/preferences`, withCredentials());
      const preferences = await handleResponse<UserPreferences>(response);
      return { preferences };
    },

    update: async (data: Partial<UserPreferences>) => {
      const response = await authFetch(`${API_BASE}/preferences`, withCredentials({
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }));
      const preferences = await handleResponse<UserPreferences>(response);
      return { preferences };
    },
  },

  // Plans and Stripe (launch redesign 4a/4c, 4 Nov 2026). Checkout and the portal answer 503
  // `billing_unavailable` until Stripe's keys are installed: `null` then, so the screen can say so calmly.
  billing: {
    plans: async () => {
      const response = await fetch(`${API_BASE}/billing/plans`);
      return (await handleResponse<{ plans: BillingPlan[] }>(response)).plans;
    },

    checkout: async (planSlug: string, interval: BillingInterval) => {
      const response = await authFetch(`${API_BASE}/billing/checkout`, withCredentials({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ planSlug, interval }),
      }));
      if (response.status === 503) return null;
      return (await handleResponse<{ url: string }>(response)).url;
    },

    portal: async () => {
      const response = await authFetch(`${API_BASE}/billing/portal`, withCredentials({ method: "POST" }));
      if (response.status === 503) return null;
      return (await handleResponse<{ url: string }>(response)).url;
    },
  },

  // Assets
  assets: {
    list: async (documentId: string) => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/assets`, withCredentials());
      const assets = await handleResponse<Asset[]>(response);
      return { assets };
    },

    getUploadUrl: async (documentId: string, data: GetUploadUrlInput) => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/assets`, withCredentials({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fileName: data.filename,
          fileType: data.mimeType,
          fileSize: data.size,
        }),
      }));
      const result = await handleResponse<{ id: string; uploadUrl: string; publicUrl: string }>(response);
      return { uploadUrl: result.uploadUrl, assetId: result.id, s3Key: result.publicUrl };
    },

    register: async (data: RegisterAssetInput) => {
      // Assets are registered when created via getUploadUrl in our new API
      // This is a no-op for compatibility
      return { asset: { id: data.s3Key, filename: data.filename, mimeType: data.mimeType, size: data.size } as Asset };
    },

    get: async (documentId: string, id: string) => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/assets/${id}`, withCredentials());
      const asset = await handleResponse<Asset>(response);
      return { asset, url: asset.url ?? "" };
    },

    delete: async (documentId: string, id: string) => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/assets/${id}`, withCredentials({
        method: "DELETE",
      }));
      if (response.ok) {
        return { success: true };
      }
      throw new Error("Failed to delete asset");
    },
  },

  // Preview
  preview: {
    getPageCount: async (documentId: string): Promise<number> => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/preview/page-count`, withCredentials());
      const data = await handleResponse<PageCountResponse>(response);
      return data.count;
    },

    getPage: async (documentId: string, page: number): Promise<PreviewResponse> => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/preview/html?page=${page}`, withCredentials());
      return handleResponse<PreviewResponse>(response);
    },

    getSections: async (documentId: string): Promise<Section[]> => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/preview/sections`, withCredentials());
      const data = await handleResponse<SectionsResponse>(response);
      return data.sections;
    },

    getLatex: async (documentId: string): Promise<PreviewResponse> => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/preview/latex`, withCredentials());
      return handleResponse<PreviewResponse>(response);
    },

    getFullHtml: async (documentId: string): Promise<PreviewResponse> => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/preview/html/full`, withCredentials());
      return handleResponse<PreviewResponse>(response);
    },
  },

  // Formulas
  formulas: {
    list: async (params?: FormulaSearchParams) => {
      const searchParams = new URLSearchParams();
      if (params?.query) searchParams.set("query", params.query);
      if (params?.category) searchParams.set("category", params.category);
      if (params?.subcategory) searchParams.set("subcategory", params.subcategory);
      if (params?.favoritesOnly) searchParams.set("favoritesOnly", "true");
      if (params?.includeSystem !== undefined) searchParams.set("includeSystem", String(params.includeSystem));
      if (params?.page) searchParams.set("page", String(params.page));
      if (params?.pageSize) searchParams.set("pageSize", String(params.pageSize));
      if (params?.theme) searchParams.set("theme", params.theme);

      const response = await authFetch(
        `${API_BASE}/formulas?${searchParams.toString()}`,
        withCredentials()
      );
      return handleResponse<{ items: Formula[]; totalCount: number; page: number; pageSize: number }>(response);
    },

    themes: async () => {
      const response = await authFetch(`${API_BASE}/formulas/themes`, withCredentials());
      return handleResponse<FormulaThemeCount[]>(response);
    },

    get: async (id: string) => {
      const response = await authFetch(`${API_BASE}/formulas/${id}`, withCredentials());
      return handleResponse<Formula>(response);
    },

    create: async (data: CreateFormulaInput) => {
      const response = await authFetch(`${API_BASE}/formulas`, withCredentials({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }));
      return handleResponse<Formula>(response);
    },

    update: async (id: string, data: UpdateFormulaInput) => {
      const response = await authFetch(`${API_BASE}/formulas/${id}`, withCredentials({
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }));
      return handleResponse<Formula>(response);
    },

    delete: async (id: string) => {
      const response = await authFetch(`${API_BASE}/formulas/${id}`, withCredentials({
        method: "DELETE",
      }));
      if (response.ok) {
        return { success: true };
      }
      throw new Error("Failed to delete formula");
    },

    toggleFavorite: async (id: string) => {
      const response = await authFetch(`${API_BASE}/formulas/${id}/favorite`, withCredentials({
        method: "POST",
      }));
      return handleResponse<Formula>(response);
    },

    use: async (id: string) => {
      const response = await authFetch(`${API_BASE}/formulas/${id}/use`, withCredentials({
        method: "POST",
      }));
      return handleResponse<{ formula: Formula; lml: string }>(response);
    },

    categories: async () => {
      const response = await authFetch(`${API_BASE}/formulas/categories`, withCredentials());
      return handleResponse<FormulaCategory[]>(response);
    },
  },

  // Diagnostic captures — user-triggered state + log bundles from
  // the math editor's DevTools panel. POST stores under a short
  // refToken; GET retrieves the full bundle by token (owner-only).
  diagnostics: {
    capture: async (body: {
      source: string;
      note?: string;
      userAgent?: string;
      url?: string;
      payload: unknown;
    }) => {
      const response = await authFetch(`${API_BASE}/diagnostics/captures`, withCredentials({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }));
      return handleResponse<{ id: string; refToken: string; createdAt: string }>(response);
    },
    getByToken: async (refToken: string) => {
      const response = await authFetch(`${API_BASE}/diagnostics/captures/${encodeURIComponent(refToken)}`, withCredentials());
      return handleResponse<{
        id: string; userId: string | null; refToken: string;
        source: string; note: string | null;
        userAgent: string | null; url: string | null;
        payload: unknown; createdAt: string;
      }>(response);
    },
    /** List the requester's own captures, newest first. */
    listMine: async (params?: { limit?: number }) => {
      const qs = new URLSearchParams();
      if (params?.limit) qs.set("limit", String(params.limit));
      const response = await authFetch(`${API_BASE}/diagnostics/captures?${qs}`, withCredentials());
      return handleResponse<Array<{
        id: string; userId: string | null; refToken: string;
        source: string; note: string | null;
        userAgent: string | null; url: string | null;
        payload: unknown; createdAt: string;
      }>>(response);
    },
  },

  // Snippets
  snippets: {
    list: async (params?: SnippetSearchParams) => {
      const searchParams = new URLSearchParams();
      if (params?.query) searchParams.set("query", params.query);
      if (params?.category) searchParams.set("category", params.category);
      if (params?.favoritesOnly) searchParams.set("favoritesOnly", "true");
      if (params?.includeSystem !== undefined) searchParams.set("includeSystem", String(params.includeSystem));
      if (params?.page) searchParams.set("page", String(params.page));
      if (params?.pageSize) searchParams.set("pageSize", String(params.pageSize));

      const response = await authFetch(
        `${API_BASE}/snippets?${searchParams.toString()}`,
        withCredentials()
      );
      return handleResponse<{ items: Snippet[]; totalCount: number; page: number; pageSize: number }>(response);
    },

    get: async (id: string) => {
      const response = await authFetch(`${API_BASE}/snippets/${id}`, withCredentials());
      return handleResponse<Snippet>(response);
    },

    create: async (data: CreateSnippetInput) => {
      const response = await authFetch(`${API_BASE}/snippets`, withCredentials({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }));
      return handleResponse<Snippet>(response);
    },

    update: async (id: string, data: UpdateSnippetInput) => {
      const response = await authFetch(`${API_BASE}/snippets/${id}`, withCredentials({
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }));
      return handleResponse<Snippet>(response);
    },

    delete: async (id: string) => {
      const response = await authFetch(`${API_BASE}/snippets/${id}`, withCredentials({
        method: "DELETE",
      }));
      if (response.ok) {
        return { success: true };
      }
      throw new Error("Failed to delete snippet");
    },

    toggleFavorite: async (id: string) => {
      const response = await authFetch(`${API_BASE}/snippets/${id}/favorite`, withCredentials({
        method: "POST",
      }));
      return handleResponse<Snippet>(response);
    },

    use: async (id: string) => {
      const response = await authFetch(`${API_BASE}/snippets/${id}/use`, withCredentials({
        method: "POST",
      }));
      return handleResponse<{ success: boolean }>(response);
    },

    categories: async () => {
      const response = await authFetch(`${API_BASE}/snippets/categories`, withCredentials());
      return handleResponse<string[]>(response);
    },
  },

  // Draft blocks (per-user scratchpad/library)
  draftBlocks: {
    list: async (params?: { type?: string; category?: string; favoritesOnly?: boolean; query?: string; page?: number; pageSize?: number }) => {
      const sp = new URLSearchParams();
      if (params?.type) sp.set("type", params.type);
      if (params?.category) sp.set("category", params.category);
      if (params?.favoritesOnly) sp.set("favoritesOnly", "true");
      if (params?.query) sp.set("query", params.query);
      if (params?.page) sp.set("page", String(params.page));
      if (params?.pageSize) sp.set("pageSize", String(params.pageSize));
      const response = await authFetch(`${API_BASE}/draft-blocks?${sp.toString()}`, withCredentials());
      return handleResponse<{ items: DraftBlock[]; totalCount: number; page: number; pageSize: number }>(response);
    },
    get: async (id: string) => {
      const response = await authFetch(`${API_BASE}/draft-blocks/${id}`, withCredentials());
      return handleResponse<DraftBlock>(response);
    },
    create: async (data: { type: string; content?: Record<string, unknown>; name?: string; category?: string; tags?: string[] }) => {
      const response = await authFetch(`${API_BASE}/draft-blocks`, withCredentials({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }));
      return handleResponse<DraftBlock>(response);
    },
    update: async (id: string, data: { name?: string; type?: string; content?: Record<string, unknown>; category?: string; tags?: string[] }) => {
      const response = await authFetch(`${API_BASE}/draft-blocks/${id}`, withCredentials({
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }));
      return handleResponse<DraftBlock>(response);
    },
    delete: async (id: string) => {
      const response = await authFetch(`${API_BASE}/draft-blocks/${id}`, withCredentials({ method: "DELETE" }));
      if (response.ok) return { success: true };
      throw new Error("Failed to delete draft block");
    },
    toggleFavorite: async (id: string) => {
      const response = await authFetch(`${API_BASE}/draft-blocks/${id}/favorite`, withCredentials({ method: "POST" }));
      return handleResponse<void>(response);
    },
    commit: async (id: string, data: { documentId: string; sortOrder?: number; parentId?: string; depth?: number }) => {
      const response = await authFetch(`${API_BASE}/draft-blocks/${id}/commit`, withCredentials({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }));
      return handleResponse<{ blockId: string }>(response);
    },
    createFromBlock: async (data: { documentId: string; blockId: string }) => {
      const response = await authFetch(`${API_BASE}/draft-blocks/from-block`, withCredentials({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }));
      return handleResponse<DraftBlock>(response);
    },
    categories: async () => {
      const response = await authFetch(`${API_BASE}/draft-blocks/categories`, withCredentials());
      return handleResponse<string[]>(response);
    },
  },

  // Shared document (public, no auth required)
  getShared: async (shareLink: string) => {
    const response = await authFetch(`${API_BASE}/documents/shared/${shareLink}`);
    const document = await handleResponse<DocumentWithBlocks>(response);
    return { document };
  },

  // Sharing
  sharing: {
    enable: async (documentId: string, isPublic: boolean) => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/share`, withCredentials({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isPublic }),
      }));
      return handleResponse<ShareResult>(response);
    },

    revoke: async (documentId: string) => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/share`, withCredentials({
        method: "DELETE",
      }));
      if (response.ok) {
        return { success: true };
      }
      throw new Error("Failed to revoke share link");
    },
  },

  // Collaborators
  collaborators: {
    list: async (documentId: string) => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/collaborators`, withCredentials());
      return handleResponse<CollaboratorList>(response);
    },

    invite: async (documentId: string, data: { email: string; role: string }) => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/collaborators/invite`, withCredentials({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }));
      return handleResponse<InviteResult>(response);
    },

    updateRole: async (documentId: string, userId: string, data: { role: string }) => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/collaborators/users/${userId}`, withCredentials({
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }));
      return handleResponse<UserCollaborator>(response);
    },

    remove: async (documentId: string, userId: string) => {
      const response = await authFetch(`${API_BASE}/documents/${documentId}/collaborators/users/${userId}`, withCredentials({
        method: "DELETE",
      }));
      if (response.ok) {
        return { success: true };
      }
      throw new Error("Failed to remove collaborator");
    },
  },

  // Teams
  teams: {
    list: async () => {
      const response = await authFetch(`${API_BASE}/teams`, withCredentials());
      const teams = await handleResponse<Team[]>(response);
      return { teams };
    },

    get: async (id: string) => {
      const response = await authFetch(`${API_BASE}/teams/${id}`, withCredentials());
      const team = await handleResponse<TeamDetails>(response);
      return { team };
    },

    create: async (data: CreateTeamInput) => {
      const response = await authFetch(`${API_BASE}/teams`, withCredentials({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }));
      const team = await handleResponse<Team>(response);
      return { team };
    },

    // FT-SANDBOX-SCOPE: mint a real-but-throwaway playground team (seeded
    // Everyone group + owner membership, is_playground=true). Excluded from
    // lists/seat-quotas server-side, but loadable by id — the authed teams
    // playground runs the real TeamManageDialog against it.
    createPlayground: async () => {
      const response = await authFetch(`${API_BASE}/teams/playground`, withCredentials({
        method: "POST",
      }));
      const team = await handleResponse<Team>(response);
      return { team };
    },

    update: async (id: string, data: Partial<CreateTeamInput>) => {
      const response = await authFetch(`${API_BASE}/teams/${id}`, withCredentials({
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }));
      const team = await handleResponse<Team>(response);
      return { team };
    },

    delete: async (id: string) => {
      const response = await authFetch(`${API_BASE}/teams/${id}`, withCredentials({
        method: "DELETE",
      }));
      if (response.ok) {
        return { success: true };
      }
      throw new Error("Failed to delete team");
    },

    members: {
      list: async (teamId: string) => {
        const response = await authFetch(`${API_BASE}/teams/${teamId}/members`, withCredentials());
        const members = await handleResponse<TeamMember[]>(response);
        return { members };
      },

      invite: async (teamId: string, data: InviteTeamMemberInput) => {
        const response = await authFetch(`${API_BASE}/teams/${teamId}/members`, withCredentials({
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        }));
        const member = await handleResponse<TeamMember>(response);
        return { member };
      },

      updateRole: async (teamId: string, userId: string, data: { role: string }) => {
        const response = await authFetch(`${API_BASE}/teams/${teamId}/members/${userId}`, withCredentials({
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        }));
        const member = await handleResponse<TeamMember>(response);
        return { member };
      },

      remove: async (teamId: string, userId: string) => {
        const response = await authFetch(`${API_BASE}/teams/${teamId}/members/${userId}`, withCredentials({
          method: "DELETE",
        }));
        if (response.ok) {
          return { success: true };
        }
        throw new Error("Failed to remove team member");
      },
    },

    documents: async (teamId: string) => {
      const response = await authFetch(`${API_BASE}/teams/${teamId}/documents`, withCredentials());
      const documents = await handleResponse<Document[]>(response);
      return { documents };
    },
  },

  // Help / Documentation
  help: {
    list: async (category?: string) => {
      const params = category ? `?category=${encodeURIComponent(category)}` : "";
      const response = await authFetch(`${API_BASE}/lilia/help${params}`, withCredentials());
      return handleResponse<HelpArticleListItem[]>(response);
    },

    categories: async () => {
      const response = await authFetch(`${API_BASE}/lilia/help/categories`, withCredentials());
      return handleResponse<HelpCategory[]>(response);
    },

    getById: async (id: string) => {
      const response = await authFetch(`${API_BASE}/lilia/help/${id}`, withCredentials());
      return handleResponse<HelpArticleDetail>(response);
    },

    getBySlug: async (slug: string) => {
      const response = await authFetch(`${API_BASE}/lilia/help/slug/${encodeURIComponent(slug)}`, withCredentials());
      return handleResponse<HelpArticleDetail>(response);
    },

    search: async (query: string) => {
      const response = await authFetch(`${API_BASE}/lilia/help/search?q=${encodeURIComponent(query)}`, withCredentials());
      return handleResponse<HelpArticleListItem[]>(response);
    },
  },

  // AI — the in-app "document architect". A single conversational endpoint:
  // the editor sends the document id + the running message transcript, and the
  // hosted agent replies with a brief message plus a set of typed-block
  // OPERATIONS (a reviewable diff). The endpoint NEVER mutates the document —
  // the editor applies accepted ops via api.blocks.*. Gating is surfaced as a
  // 403 with `locked: true` (no AI key, not entitled, over budget, or disabled)
  // which we rethrow as an `AiArchitectLockedError` so the screen can render
  // the locked / disabled state instead of a generic failure.
  ai: {
    architect: async (
      req: AiArchitectRequest,
      signal?: AbortSignal,
    ): Promise<AiArchitectResponse> => {
      const response = await authFetch(`${API_BASE}/ai/architect`, withCredentials({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(req),
        signal,
      }));
      if (response.status === 403) {
        const body = (await response.json().catch(() => ({}))) as Partial<AiArchitectLocked>;
        throw new AiArchitectLockedError({
          locked: true,
          reason: body.reason ?? "not-entitled",
          message: body.message ?? "AI chat is not available on your plan.",
        });
      }
      return handleResponse<AiArchitectResponse>(response);
    },
    /** Grammar and style suggestions for the document's prose (the editor's ⌘⇧G). */
    grammarCheck: async (text: string): Promise<{ suggestions: GrammarSuggestion[] }> => {
      const response = await authFetch(`${API_BASE}/ai/grammar-check`, withCredentials({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      }));
      return handleResponse<{ suggestions: GrammarSuggestion[] }>(response);
    },
    /** The selectable model catalog for the current user (picker source). */
    models: async (signal?: AbortSignal): Promise<AiModelsResponse> => {
      const response = await authFetch(`${API_BASE}/ai/models`, withCredentials({ signal }));
      return handleResponse<AiModelsResponse>(response);
    },
    /**
     * Ask Lilia — the natural-language front door. Routes the message to a skill
     * (citations, compile-doctor, equation, table, …) and runs it; the reply
     * carries the artifact (LML / BibTeX / LaTeX in fenced blocks). 403 => gated.
     */
    ask: async (req: AskLiliaRequest, signal?: AbortSignal): Promise<AskLiliaResponse> => {
      const response = await authFetch(`${API_BASE}/ai/ask`, withCredentials({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ language: i18n.language, ...req }),
        signal,
      }));
      if (response.status === 403) {
        const body = (await response.json().catch(() => ({}))) as Partial<AiArchitectLocked>;
        throw new AiArchitectLockedError({
          locked: true,
          reason: body.reason ?? "not-entitled",
          message: body.message ?? "Ask Lilia is not available on your plan.",
        });
      }
      return handleResponse<AskLiliaResponse>(response);
    },
    /**
     * Durable Ask Lilia conversations — server is the source of truth; the panel
     * keeps a localStorage cache only for instant paint. Scoped by document,
     * with move (reassign) and clone (copy/promote) across documents.
     */
    conversations: {
      list: async (documentId?: string, signal?: AbortSignal): Promise<AiConversationListItem[]> => {
        const qs = documentId ? `?documentId=${encodeURIComponent(documentId)}` : "";
        const response = await authFetch(`${API_BASE}/ai/conversations${qs}`, withCredentials({ signal }));
        return handleResponse<AiConversationListItem[]>(response);
      },
      get: async (id: string, signal?: AbortSignal): Promise<AiConversation> => {
        const response = await authFetch(`${API_BASE}/ai/conversations/${id}`, withCredentials({ signal }));
        return handleResponse<AiConversation>(response);
      },
      create: async (req: { documentId?: string | null; title?: string }, signal?: AbortSignal): Promise<AiConversation> => {
        const response = await authFetch(`${API_BASE}/ai/conversations`, withCredentials({
          method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(req), signal,
        }));
        return handleResponse<AiConversation>(response);
      },
      append: async (id: string, msg: { role: string; content: unknown; creditsUsed?: number }, signal?: AbortSignal): Promise<AiConversationMessage> => {
        const response = await authFetch(`${API_BASE}/ai/conversations/${id}/messages`, withCredentials({
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ role: msg.role, content: msg.content, creditsUsed: msg.creditsUsed ?? 0 }), signal,
        }));
        return handleResponse<AiConversationMessage>(response);
      },
      rename: async (id: string, title: string): Promise<void> => {
        await authFetch(`${API_BASE}/ai/conversations/${id}/rename`, withCredentials({
          method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ title }),
        }));
      },
      move: async (id: string, documentId: string | null): Promise<void> => {
        await authFetch(`${API_BASE}/ai/conversations/${id}/move`, withCredentials({
          method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ documentId }),
        }));
      },
      clone: async (id: string, req: { documentId?: string | null; title?: string }): Promise<AiConversation> => {
        const response = await authFetch(`${API_BASE}/ai/conversations/${id}/clone`, withCredentials({
          method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(req),
        }));
        return handleResponse<AiConversation>(response);
      },
      remove: async (id: string): Promise<void> => {
        await authFetch(`${API_BASE}/ai/conversations/${id}`, withCredentials({ method: "DELETE" }));
      },
    },
    /** Classify a message to a skill without generating — for live suggestions / a preview chip. */
    askRoute: async (message: string, proficiency?: string, signal?: AbortSignal): Promise<AskRouteResponse> => {
      const response = await authFetch(`${API_BASE}/ai/ask/route`, withCredentials({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message, proficiency }),
        signal,
      }));
      return handleResponse<AskRouteResponse>(response);
    },
    /** Natural-language → LaTeX for the math editor's AI input. Returns the
     *  generated LaTeX (and a ready equation block). */
    suggestEquation: async (description: string, signal?: AbortSignal): Promise<{ latex: string; block?: unknown }> => {
      const response = await authFetch(`${API_BASE}/ai/suggest-equation`, withCredentials({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ description }),
        signal,
      }));
      return handleResponse<{ latex: string; block?: unknown }>(response);
    },
    /** The skill catalog — drives an Ask Lilia picker / context-aware suggested prompts. */
    skills: async (signal?: AbortSignal): Promise<AiSkillInfo[]> => {
      const response = await authFetch(`${API_BASE}/ai/skills`, withCredentials({ signal }));
      return handleResponse<AiSkillInfo[]>(response);
    },
  },

  // Standalone tools — the "Open in Lilia" handoff. The tools host writes the
  // artifact and stops; the editor pulls it here, so the public tools surface
  // never holds a reference to document ownership.
  // (artifact) into a real document owned by the signed-in user.
  tools: {
    toDocument: async (artifactId: string): Promise<{ documentId: string }> => {
      const response = await authFetch(
        `${API_BASE}/from-tool/${artifactId}`,
        withCredentials({ method: "POST" }),
      );
      return handleResponse<{ documentId: string }>(response);
    },
  },

  // Document themes (design-handoffs/2026-10-05-document-themes). The
  // descriptors are server-owned: the same ones generate lilia-theme.sty, so
  // the editor never hard-codes a theme's colours or fonts.
  themes: {
    list: async (): Promise<ThemeDescriptor[]> => {
      const response = await authFetch(`${API_BASE}/themes`, withCredentials());
      return handleResponse<ThemeDescriptor[]>(response);
    },
  },

  publishers: {
    list: async (): Promise<PublisherCatalog> => {
      const response = await authFetch(`${API_BASE}/publishers`, withCredentials());
      return handleResponse<PublisherCatalog>(response);
    },
    /** Your documents written for a publisher, each with the publisher check's verdict (the dashboard). */
    documents: async (): Promise<{ documents: PublisherDocumentStatus[]; limit: number }> => {
      const response = await authFetch(`${API_BASE}/publishers/documents`, withCredentials());
      return handleResponse<{ documents: PublisherDocumentStatus[]; limit: number }>(response);
    },
  },

  /** In-app notifications (sharing: shared with you, invite accepted, role changed, access removed). */
  notifications: {
    list: async (page = 1, pageSize = 30): Promise<{ items: AppNotification[]; totalCount: number }> => {
      const response = await authFetch(`${API_BASE}/notifications?page=${page}&pageSize=${pageSize}`, withCredentials());
      return handleResponse<{ items: AppNotification[]; totalCount: number }>(response);
    },
    count: async (): Promise<number> => {
      const response = await authFetch(`${API_BASE}/notifications/count`, withCredentials());
      const body = await handleResponse<{ count?: number; unread?: number } | number>(response);
      return typeof body === "number" ? body : body.count ?? body.unread ?? 0;
    },
    markRead: async (id: string): Promise<void> => {
      await authFetch(`${API_BASE}/notifications/${id}/read`, withCredentials({ method: "PUT" }));
    },
    markAllRead: async (): Promise<void> => {
      await authFetch(`${API_BASE}/notifications/read-all`, withCredentials({ method: "PUT" }));
    },
  },
};

/** One in-app notification, as GET /api/notifications lists it. */
export interface AppNotification {
  id: string;
  /** document_shared | invite_accepted | role_changed | access_removed | … */
  type: string;
  title: string;
  message?: string | null;
  link?: string | null;
  isRead: boolean;
  createdAt: string;
}

// Document themes ------------------------------------------------------------

export type ThemeId = "classic" | "cerulean" | "index" | "carnet" | "gazette" | "exposition";

/** "theme" uses the theme's own paper colour; "white" forces white paper. */
export type LookPaper = "theme" | "white";

/** How a paper draws its tables (2f): booktabs rules, stripes, or a filled header. */
export type TableStyle = "ruled" | "banded" | "header";
export type TableDensity = "normal" | "compact";
export type CaptionPosition = "above" | "below";

/**
 * Look → Tables: a paper setting, never a table's. Each part is optional on
 * the wire: a missing style is the theme's `tablesDefault`, a missing density
 * is normal, a missing caption position is above.
 */
export interface LookTables {
  style?: TableStyle;
  density?: TableDensity;
  caption?: CaptionPosition;
}

/** A document's look. `null` on the document means Classic. */
export interface DocumentLook {
  theme: ThemeId;
  paper: LookPaper;
  /** Top-level heading block id → index into the theme's `sequence` (Index). */
  pins: Record<string, number>;
  /** How this paper's tables look. Under a publisher class only Ruled. */
  tables?: LookTables;
}

/** One theme, as GET /api/themes describes it. */
export interface ThemeDescriptor {
  id: ThemeId;
  name: string;
  /** Who it is for, e.g. "Course notes, lecture series, handbooks". */
  for: string;
  available: boolean;
  status?: "ready" | "planned";
  fonts: { heading: string; body: string; headingCss: string; bodyCss: string };
  colours: { paper: string; ink: string; accent: string; heading: string; kicker: string };
  printSafe: { paper: string; ink: string };
  /** The chapter colour sequence (Index only). */
  sequence: string[] | null;
  /** The table style a paper in this theme gets until it picks one ("ruled", "banded", "header"). */
  tablesDefault: TableStyle | string;
  /** How the paper colours its tables, on the theme's paper and on white (paper "white" or Print-safe). */
  table?: { onPaper: ThemeTablePalette; onWhite: ThemeTablePalette } | null;
  /** The classes this theme is for (Exposition: ["beamer"]); null for any class. */
  classes?: string[] | null;
}

/** One paper's table colours, as lilia-theme.sty prints them. "chapter" means Index's chapter colour. */
export interface ThemeTablePalette {
  /** The stripe hue (Banded). */
  hue: string;
  /** The stripe strength, in percent of the hue over the paper. */
  band: number;
  /** The header band's fill (Header band). */
  head: string;
  /** The ink on the header band. */
  headInk: string;
}

// Pagination types
export interface PaginatedResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
}

export interface OutlineItem {
  title: string;
  level: number;
}

export interface DocumentListItem {
  id: string;
  title: string;
  ownerId: string;
  ownerName?: string;
  teamId?: string;
  teamName?: string;
  createdAt: string;
  updatedAt: string;
  lastOpenedAt?: string;
  blockCount: number;
  sectionCount: number;
  outline: OutlineItem[];
  labels: DocumentLabel[];
  role?: string; // "owner", "editor", "viewer"
  validationErrorCount?: number;
  validationWarningCount?: number;
  validationCheckedAt?: string | null;
  /** AI-generated one-sentence gist (phase 2a). Null until generated. */
  aiSummary?: string | null;
  /** The document theme, for the card's schematic page (2c). Null means Classic. */
  look?: DocumentLook | null;
}

export interface TrashDocumentItem {
  id: string;
  title: string;
  ownerId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
  daysUntilPurge: number;
}

export type ViewMode = "cards" | "list" | "table";
export type DensityMode = "compact" | "cozy" | "comfortable";

// Types
export interface Document {
  id: string;
  title: string;
  ownerId?: string;
  /** Whose it is — for a viewer's "View only · shared by …" chip (API #164). */
  ownerName?: string | null;
  ownerEmail?: string | null;
  /** The reader's role on it: "owner" | "editor" | "viewer" (API #160). */
  role?: string;
  teamId?: string;
  /** The language the PDF is typeset in: a code, or "auto" (read from the text). API #350. */
  language?: string;
  /** With language "auto": what the text reads as. */
  detectedLanguage?: string | null;
  /** The publisher profile (venue + stage), null when none (API #373). */
  publisherProfile?: string | null;
  paperSize?: string;
  /** Page orientation — "portrait" (default) or "landscape". Drives the
   *  `landscape` documentclass option in BuildClassDirective. */
  orientation?: "portrait" | "landscape";
  /** L/C/R header & footer slots. When any is set, BuildLayoutPreamble
   *  emits the matching \lhead/\chead/\rhead/\lfoot/\cfoot/\rfoot and
   *  loads fancyhdr. Legacy headerText/footerText still work when none
   *  of the six slots is set. */
  headerLeft?: string;
  headerCenter?: string;
  headerRight?: string;
  footerLeft?: string;
  footerCenter?: string;
  footerRight?: string;
  /** Page layout as the API returns it (DocumentDto). */
  marginTop?: string | null;
  marginBottom?: string | null;
  marginLeft?: string | null;
  marginRight?: string | null;
  headerText?: string | null;
  footerText?: string | null;
  lineSpacing?: number | null;
  paragraphIndent?: string | null;
  pageNumbering?: string | null;
  /** Numbered heading levels: null = the class default, 0 = none, 1–5 = levels 1 to n. The table of
   *  contents lists the same levels. */
  headingNumbering?: number | null;
  fontFamily?: string;
  fontSize?: number;
  columns?: number;
  columnSeparator?: string;
  columnGap?: number;
  balancedColumns?: boolean;
  isPublic?: boolean;
  shareLink?: string;
  shareSlug?: string;
  status?: "draft" | "saved" | "published";
  lastAutoSavedAt?: string;
  lastOpenedAt?: string;
  aiEnabled?: boolean;
  experimentalLatexEdit?: boolean;
  customPreamble?: string;
  /** The document theme; null (or absent) means Classic. */
  look?: DocumentLook | null;
  /** Why themes are off for this document (a publisher class), or null. */
  lookLocked?: string | null;
  /** The themes this document's class may use, in catalog order (beamer: Classic and Exposition). */
  lookThemes?: ThemeId[] | null;
  createdAt: string;
  updatedAt: string;
  /** Optimistic-concurrency version — carried by the Flow sync worker
   *  into each background sync. See 2026-05-21-flow-editor-save-model.md. */
  version?: number;
}

export interface Block {
  id: string;
  documentId?: string;
  type: string;
  content: Record<string, unknown>;
  sortOrder: number;
  parentId?: string;
  depth?: number;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Block-grouping primitive (LILIA-136). M:N relationship between blocks
 * and named groups, scoped to a document and a single "dimension".
 * First dimension is "layout" (multi-column regions); the data model
 * is designed to host other dimensions (review tags, counter scopes,
 * style presets, source-attribution, conditional content) without
 * schema or shape churn.
 *
 * A block belongs to at most one group per dimension but can be in
 * many groups across dimensions simultaneously.
 */
export interface BlockGroup {
  id: string;
  documentId: string;
  /** "layout" | "review" | "numbering" | ... — see BlockGroupDimensions on the API. */
  dimension: string;
  /** Per-dimension JSON. For "layout": { columns: 1|2|3 }. */
  attributes: Record<string, unknown>;
  /** Optional user-facing name (e.g. "Body 2-col", "Abstract"). */
  name?: string;
  /** Block ids that belong to this group, in arbitrary order. */
  memberBlockIds: string[];
  createdAt: string;
  updatedAt: string;
}

/** Layout-dimension attribute shape — narrowly typed for ergonomics. */
export interface LayoutGroupAttributes {
  columns?: 1 | 2 | 3;
}

export interface CreateBlockGroupInput {
  dimension: string;
  attributes: Record<string, unknown>;
  name?: string;
  memberBlockIds: string[];
}

export interface UpdateBlockGroupInput {
  attributes?: Record<string, unknown>;
  name?: string;
  /** Pass an array (incl. empty) to replace memberships; pass undefined to leave alone. */
  memberBlockIds?: string[];
}

export interface BatchUpdateBlock {
  id: string;
  type?: string;
  content?: Record<string, unknown>;
  sortOrder?: number;
  parentId?: string;
  depth?: number;
}

export interface DocumentWithBlocks extends Document {
  blocks: Block[];
  bibliography?: Bibliography[];
  labels?: Label[];
}

export interface Bibliography {
  id: string;
  documentId?: string;
  citeKey: string;
  entryType: string;
  data: Record<string, unknown>;
  formattedText?: string;
  fields?: Record<string, string>;
  rawBibtex?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateBibliographyInput {
  citeKey: string;
  entryType: string;
  data: Record<string, unknown>;
}

export interface DoiLookupResult {
  citeKey: string;
  entryType: string;
  data: Record<string, unknown>;
}

export interface Label {
  id: string;
  label: string;
  labelType: string;
  number?: number | null;
  blockId?: string;
}

export interface DocumentLabel {
  id: string;
  name: string;
  color?: string;
  createdAt?: string;
}

export interface CreateLabelInput {
  name: string;
  color?: string;
}

export interface Job {
  id: string;
  documentId?: string;
  jobType: "IMPORT" | "EXPORT" | "CONVERT";
  direction: "INBOUND" | "OUTBOUND";
  sourceFormat: string;
  targetFormat: string;
  status: "PENDING" | "PROCESSING" | "COMPLETED" | "FAILED" | "CANCELLED";
  progress: number;
  inputFileName?: string;
  outputFileName?: string;
  errorMessage?: string;
  createdAt: string;
}

export interface Template {
  id: string;
  userId?: string;
  name: string;
  description?: string;
  category?: string;
  thumbnail?: string;
  isPublic: boolean;
  isSystem: boolean;
  usageCount: number;
  createdAt: string;
  updatedAt?: string;
}

export interface TemplateWithBlocks extends Template {
  content: Record<string, unknown>;
  blocks?: unknown[];
  documentSettings?: Record<string, unknown>;
}

export interface TemplateCategory {
  name: string;
  count: number;
}

export interface UserPreferences {
  userId?: string;
  theme: string;
  defaultFontFamily?: string;
  defaultFontSize?: number;
  defaultPaperSize?: string;
  autoSaveEnabled?: boolean;
  autoSaveInterval?: number;
  keyboardShortcuts?: Record<string, unknown>;
  /** Pro or Fun — the saved moment's style (saved-moment handoff §4). */
  personality?: "pro" | "fun";
  /** The Lilia menu's pinned tools, in order. Null until the user changes them. */
  pinnedTools?: string[] | null;
  /** How Ask Lilia explains (and where help pages start). Null until the author answers once. */
  askLevel?: Proficiency | null;
  // Legacy fields for backwards compatibility
  fontSize?: number;
  fontFamily?: string;
  defaultLanguage?: string;
  defaultExportFormat?: string;
  sidebarCollapsed?: boolean;
  previewEnabled?: boolean;
  updatedAt?: string;
}

export interface Asset {
  id: string;
  documentId?: string;
  fileName?: string;
  filename?: string; // For backwards compatibility
  fileType?: string;
  mimeType?: string; // For backwards compatibility
  fileSize?: number;
  size?: number; // For backwards compatibility
  storageKey?: string;
  url?: string;
  width?: number;
  height?: number;
  createdAt?: string;
}

export interface Team {
  id: string;
  name: string;
  slug?: string;
  image?: string;
  ownerId: string;
  ownerName?: string;
  memberCount?: number;
  createdAt: string;
  updatedAt: string;
  /** True when this is the caller's auto-minted default team
   *  (User.DefaultTeamId on the API side). The Teams page pins it
   *  to the top with a "Default" badge. */
  isDefault?: boolean;
}

export interface TeamDetails extends Team {
  members: TeamMember[];
}

export interface TeamMember {
  userId: string;
  name?: string;
  email?: string;
  image?: string;
  roleName: string;
  joinedAt?: string;
}

export interface CreateTeamInput {
  name: string;
  slug?: string;
  image?: string;
}

export interface InviteTeamMemberInput {
  email: string;
  role: string;
}

// Input types
export interface CreateDocumentInput {
  title?: string;
  teamId?: string;
  language?: string;
  paperSize?: string;
  fontFamily?: string;
  fontSize?: number;
  templateId?: string;
  // documentclass-first fields (LILIA-121 contract)
  documentCategory?: "article" | "book" | "report" | "cv";
  documentClass?:
    | "article"
    | "amsart"
    | "IEEEtran"
    | "acmart"
    | "exam"
    | "book"
    | "tufte-book"
    | "report"
    | "memoir"
    | "moderncv"
    | "altacv"
    | "resume";
  columns?: 1 | 2;
  balancedColumns?: boolean;
  sides?: "one" | "two";
  titlePage?: boolean;
  orientation?: "portrait" | "landscape";
}

export interface UpdateDocumentInput {
  title?: string;
  language?: string;
  paperSize?: string;
  fontFamily?: string;
  fontSize?: number;
  columns?: number;
  columnSeparator?: string;
  columnGap?: number;
  aiEnabled?: boolean;
  experimentalLatexEdit?: boolean;
  customPreamble?: string;
  /** Page layout (geometry and spacing), as the API's UpdateDocumentDto takes them. */
  marginTop?: string;
  marginBottom?: string;
  marginLeft?: string;
  marginRight?: string;
  lineSpacing?: number;
  paragraphIndent?: string;
  pageNumbering?: string;
  /** Numbered heading levels: -1 = back to the class default, 0 = none, 1–5 = levels 1 to n. */
  headingNumbering?: number;
  /** The document theme. The API answers 400 with a message on bad values. */
  look?: DocumentLook;
  /** A publisher profile id ("Publish with…"); "" clears it. It sets the class and options. */
  publisherProfile?: string;
  blocks?: Block[];
}

export interface ImportJobOptions {
  preserveFormatting?: boolean;
  importImages?: boolean;
  importBibliography?: boolean;
  autoDetectEquations?: boolean;
  splitByHeadings?: boolean;
}

export interface ImportJobInput {
  content: string;
  format: "LML" | "LATEX" | "DOCX" | "MARKDOWN";
  filename?: string;
  title?: string;
  options?: ImportJobOptions;
}

export interface ExportJobInput {
  documentId: string;
  format: "PDF" | "DOCX" | "LATEX" | "MARKDOWN" | "HTML" | "LML";
  options?: Record<string, unknown>;
}

export interface CreateTemplateInput {
  documentId: string;
  name: string;
  description?: string;
  category?: string;
  isPublic?: boolean;
}

export interface GetUploadUrlInput {
  filename: string;
  mimeType: string;
  size: number;
  documentId?: string;
}

export interface RegisterAssetInput {
  filename: string;
  mimeType: string;
  size: number;
  s3Key: string;
  s3Bucket: string;
  contentHash: string;
  documentId?: string;
}

// Auto-save types
export interface AutoSaveInput {
  title?: string;
  blocks: Block[];
  status?: "draft" | "saved" | "published";
}

export interface AutoSaveResponse {
  id: string;
  title: string;
  status: "draft" | "saved" | "published";
  lastAutoSavedAt: string;
  updatedAt: string;
}

// Preview types
export interface PageCountResponse {
  count: number;
}

export interface Section {
  id: string;
  title: string;
  level: number;
  startPage: number;
  endPage: number;
}

export interface SectionsResponse {
  sections: Section[];
}

export interface PreviewResponse {
  content: string;
  format: "html" | "latex";
  page?: number;
  totalPages?: number;
  generatedAt: string;
  cacheKey?: string;
}

// Formula types
export interface Formula {
  id: string;
  userId?: string;
  name: string;
  description?: string;
  latexContent: string;
  lmlContent?: string;
  category: string;
  subcategory?: string;
  tags: string[];
  isFavorite: boolean;
  isSystem: boolean;
  usageCount: number;
  version: number;
  createdAt: string;
  updatedAt: string;
  /** Lilia Math editor theme — one of FORMULA_THEMES. Nullable for legacy. */
  theme?: string | null;
  /** Stable slug for system-seeded reference formulas. */
  slug?: string | null;
  /** JSON-serialised token list for the editor's Load-from-library modal. */
  tokensJson?: string | null;
}

export interface CreateFormulaInput {
  name: string;
  latexContent: string;
  category: string;
  description?: string;
  subcategory?: string;
  tags?: string[];
  theme?: string;
  tokensJson?: string;
}

export interface UpdateFormulaInput {
  name?: string;
  description?: string;
  latexContent?: string;
  category?: string;
  subcategory?: string;
  tags?: string[];
  theme?: string;
  tokensJson?: string;
}

export interface FormulaSearchParams {
  query?: string;
  category?: string;
  subcategory?: string;
  favoritesOnly?: boolean;
  includeSystem?: boolean;
  page?: number;
  pageSize?: number;
  theme?: string;
}

export interface FormulaThemeCount {
  theme: string;
  count: number;
}

export interface FormulaCategory {
  id: string;
  label: string;
  subcategories: { id: string; label: string }[];
}

// Snippet types
export interface Snippet {
  id: string;
  userId?: string;
  name: string;
  description?: string;
  latexContent: string;
  blockType: string;
  category: string;
  requiredPackages: string[];
  preamble?: string;
  tags: string[];
  isFavorite: boolean;
  isSystem: boolean;
  usageCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSnippetInput {
  name: string;
  latexContent: string;
  blockType: string;
  category: string;
  description?: string;
  requiredPackages?: string[];
  preamble?: string;
  tags?: string[];
}

export interface UpdateSnippetInput {
  name?: string;
  description?: string;
  latexContent?: string;
  blockType?: string;
  category?: string;
  requiredPackages?: string[];
  preamble?: string;
  tags?: string[];
}

export interface SnippetSearchParams {
  query?: string;
  category?: string;
  favoritesOnly?: boolean;
  includeSystem?: boolean;
  page?: number;
  pageSize?: number;
}

// Draft blocks
export interface DraftBlock {
  id: string;
  userId: string;
  name?: string;
  type: string;
  content: Record<string, unknown>;
  metadata: Record<string, unknown>;
  category?: string;
  tags: string[];
  isFavorite: boolean;
  usageCount: number;
  createdAt: string;
  updatedAt: string;
}

// Sharing types
export interface ShareResult {
  shareLink: string;
  shareSlug?: string;
  isPublic: boolean;
  /** Iter 8 — ISO string, null/undefined = "never". */
  linkExpiresAt?: string | null;
  /** Iter 8 — "view" (default) | "comment" (reserved). */
  linkPermission?: string;
}

export interface UserCollaborator {
  id: string;
  userId: string;
  name?: string;
  email?: string;
  image?: string;
  roleName: string;
  roleId: string;
  createdAt: string;
}

/** An invitation not yet accepted: pending, or expired after 14 days. */
export interface PendingInvite {
  id: string;
  email: string;
  role: string;
  status: "pending" | "expired";
  invitedAt: string;
  /** The last send; a resend moves it and the expiry. */
  sentAt: string;
  expiresAt: string;
}

export interface GroupCollaborator {
  id: string;
  groupId: string;
  groupName: string;
  roleName: string;
  roleId: string;
  memberCount: number;
  createdAt: string;
}

export interface CollaboratorList {
  users: UserCollaborator[];
  groups: GroupCollaborator[];
}

export interface InviteResult {
  success: boolean;
  userFound: boolean;
  email: string;
  message?: string;
}

// Block type metadata (from API)
export interface BlockTypeMetadata {
  type: string;
  label: string;
  description: string;
  iconName: string;
  category: string;
  defaultContent: Record<string, unknown>;
}

// Document version types
export interface DocumentVersion {
  id: string;
  documentId?: string;
  versionNumber: number;
  /**
   * Whether the document currently holds this version.
   *
   * Restoring moves this marker back rather than appending a row, so it is not
   * the same as "newest" — and it is validated server-side against the
   * document's real content, so editing clears it without any write path
   * knowing about it (lilia-editor-api #115).
   */
  isCurrent?: boolean;
  /**
   * True for both machine-written kinds: the throttled auto-save, and the
   * rescue row a restore writes when it is about to overwrite unstored work.
   * The timeline hides these by default — see versionTimeline.
   */
  isAutoSave?: boolean;
  name?: string;
  title?: string; // For backwards compatibility
  comment?: string;
  snapshot?: Record<string, unknown>;
  sizeBytes?: number;
  createdBy?: string;
  creatorName?: string;
  createdAt: string;
  creator?: {
    id: string;
    name: string;
    image?: string;
  };
}

// Help / Documentation types
export interface HelpArticleListItem {
  id: string;
  title: string;
  helpCategory: string | null;
  helpOrder: number;
  helpSlug: string | null;
}

export interface HelpArticleDetail {
  id: string;
  title: string;
  helpCategory: string | null;
  helpOrder: number;
  helpSlug: string | null;
  blocks: Block[];
}

// ───── AI document architect ─────────────────────────────────────────────────

export interface AiArchitectMessage {
  role: "user" | "assistant";
  content: string;
}

export interface AiArchitectRequest {
  documentId: string;
  messages: AiArchitectMessage[];
  /** Optional model id (validated server-side against the catalog + tier). */
  model?: string;
}

/** One selectable model from GET /api/ai/models. */
export interface AiModelInfo {
  id: string;
  provider: string;
  displayName: string;
  tier: string;            // fast | default | premium
  minMembership: string;   // free | pro | team
  contextWindow: number;
  maxOutput: number;
  supportsAttachments: boolean;
  supportsVision: boolean;
  isDefault: boolean;
  usable: boolean;         // allowed for the caller's membership
}

export interface AiModelsResponse {
  defaultModel: string;
  membership: string;
  creditsUsed?: number;
  models: AiModelInfo[];
}

/** A single typed-block operation proposed by the agent. `block.{type,content}`
 *  match the editor block registry — the same shapes the structure wizard's
 *  `specBlockToBlock` produces (heading, paragraph, equation, figure, table,
 *  theorem, code, abstract, list, bibliography, tableOfContents…). */
export interface BlockOp {
  op: "add" | "edit" | "move" | "remove";
  /** Target block id (edit / move / remove), or the id of an added block. */
  id?: string;
  /** Insert/move anchor — the block id to place this after; null = front. */
  afterId?: string | null;
  block?: {
    type: string;
    content: Record<string, unknown>;
  };
}

export interface AiArchitectUsage {
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
  credits?: number;       // model-weighted credits for this call
}

export interface AiArchitectResponse {
  reply: string;
  operations: BlockOp[];
  usage: AiArchitectUsage;
  balance?: { remainingUsd: number };
  creditsUsed?: number;   // cumulative credits consumed by the user
}

// ── Ask Lilia (the natural-language front door over the skill family) ──
export type Proficiency = "beginner" | "intermediate" | "advanced";

export interface AskLiliaRequest {
  message: string;
  proficiency?: Proficiency;
  model?: string;
  /** The document the author is editing, so Ask Lilia grounds its answer in it. */
  documentId?: string;
  /** Edit-document mode — when true the model may write to the doc via tools. */
  editMode?: boolean;
  /** Prior turns of the conversation, so Ask Lilia remembers the thread.
   * role is "user" or "assistant"; oldest first. */
  history?: { role: "user" | "assistant"; content: string }[];
  /** Binary/image files (base64) — images/PDFs go to the model natively, docx
   * is text-extracted server-side. Text files are inlined into `message`. */
  attachments?: { name: string; mediaType: string; dataBase64: string }[];
  /** Scope the turn to one TikZ figure (TikZ step 3, 1c): it reads that figure only. */
  figure?: TikzAskFigure;
  /** The interface's language (en, fr, es): the help articles Ask Lilia reads come in it. */
  language?: string;
  /** Challenge (API #356): keep the author's answers with this skill while a challenge runs. */
  continueSkill?: string;
  /** Read maths (18 Oct): the photo rides in `attachments`, pasted text in `text`. Nothing is written. */
  math?: { text?: string; afterBlockId?: string };
}

/** One formula Ask Lilia read, checked by TeX with the document's preamble (`ok`) or not (`error`). */
export interface MathAskFormula {
  latex: string;
  name?: string | null;
  /** An equation of its own (true) or maths inside a sentence (false). */
  display: boolean;
  ok: boolean;
  error?: string | null;
}

export interface MathAskProposal {
  formulas: MathAskFormula[];
  /** 1, or 2 when TeX's error went back to the model once. */
  attempts: number;
}

export interface AskLiliaResponse {
  skillId: string;          // e.g. "lilia-citations"
  skillName: string;        // e.g. "Citations"
  reply: string;            // artifact + prose (LML/BibTeX/LaTeX in fenced blocks)
  usage: AiArchitectUsage;
  balance?: { remainingUsd: number };
  creditsUsed?: number;
  /** True when the model wrote to the open document (client should reload). */
  documentChanged?: boolean;
  /** Block ids the model added/edited/reordered — the editor highlights these. */
  changedBlockIds?: string[];
  /** Version snapshot taken before the AI edited — for "Undo AI changes". */
  undoVersionId?: string;
  /** Sources the assistant cited from live web search (when web search ran). */
  citations?: { title?: string; url: string; snippet?: string }[] | null;
  /** True when the tool loop hit the round cap mid-edit (document may be hybrid). */
  partialApply?: boolean;
  /** Resolved model id for this turn (e.g. claude-sonnet-4-6). */
  model?: string | null;
  /** A figure turn's proposal, compiled before it is shown (TikZ step 3, 1c). */
  figure?: TikzAskProposal | null;
  /** A maths turn's reading, each formula checked by TeX first. */
  math?: MathAskProposal | null;
}

/** Durable Ask Lilia conversation (server is source of truth). */
export interface AiConversationListItem {
  id: string;
  documentId: string | null;
  title: string;
  messageCount: number;
  createdAt: string;
  updatedAt: string;
}
export interface AiConversationMessage {
  id: string;
  role: string;          // "user" | "lilia"
  content: unknown;      // verbatim client turn payload
  creditsUsed: number;
  sortOrder: number;
  createdAt: string;
}
export interface AiConversation {
  id: string;
  documentId: string | null;
  title: string;
  createdAt: string;
  updatedAt: string;
  messages: AiConversationMessage[];
}

export interface AskRouteResponse {
  skillId: string;
  skillName: string;
  confidence: number;
  reason: string;
  proficiency: string;
  preamble: string;
}

export interface AiSkillInfo {
  id: string;
  name: string;
  whenToUse: string;
  tier: string;
}

/** One suggestion from POST /api/ai/grammar-check. */
export interface GrammarSuggestion {
  original: string;
  suggestion: string;
  type: "grammar" | "style" | "clarity" | "passive_voice";
  explanation: string;
}

export type AiArchitectLockReason = "no-key" | "not-entitled" | "over-budget" | "disabled";

export interface AiArchitectLocked {
  locked: true;
  reason: AiArchitectLockReason;
  message: string;
}

/** Thrown by `api.ai.architect` on a 403 locked response so the screen can
 *  render the locked (upsell) / disabled (no key) state rather than a toast. */
export class AiArchitectLockedError extends Error {
  readonly locked = true as const;
  readonly reason: AiArchitectLockReason;
  constructor(payload: AiArchitectLocked) {
    super(payload.message);
    this.name = "AiArchitectLockedError";
    this.reason = payload.reason;
  }
}

export interface HelpCategory {
  category: string;
  label: string;
  articleCount: number;
}

/** One venue at one stage, as GET /api/publishers lists it (API #373). */
export interface PublisherProfile {
  id: string;
  publisher: string;
  venue: string;
  venueType: string;
  stage: "review" | "final";
  documentClass: string;
  classOptions: string;
  bibliographyStyle: string;
  templateVersion: string;
  requiredMetadata: string[];
  guidelinesUrl: string;
  /** The venue without its stage: profiles of one family are one venue at review and final. */
  family: string;
}

export interface PublisherCatalog {
  catalogVersion: string;
  profiles: PublisherProfile[];
  /** Each publisher class's version installed on the server ("2026/08/16 v2.20"), or null. */
  installed?: Record<string, string | null>;
}

/** One row of the compliance dashboard (GET /api/publishers/documents). */
export interface PublisherDocumentStatus {
  id: string;
  title: string;
  updatedAt: string;
  profile: string | null;
  publisher: string | null;
  venue: string | null;
  stage: "review" | "final" | null;
  documentClass: string;
  status: "passed" | "warnings" | "errors";
  errors: number;
  warnings: number;
  /** The worst finding, in words. */
  first: string | null;
}
